import SpilloverModal from './SpilloverModal';
import MachineDowntimeModal from './MachineDowntimeModal';
import React from 'react';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router-dom';
import { homeService } from '../../_services';
import { utilizationSettingsService } from '../../_services/utilizationSettings.service';
import { getCurrentMonthTarget, readCachedMonthlyTargets } from '../../utils/monthlyTargets';
import { groupMachinesByCategory } from '../../utils/utilizationCategories';
import { DEFAULT_AVAILABLE_HOURS, getUtilizationByTime } from '../../utils/utilizationTime';
import {
    aggregateDailyOee,
    formatDayRange,
    getFloorDeckDateLabel,
    getFloorDeckJobOrderPresentation,
    getCurrentMonthRange,
    getDowntimeDetail,
    getDowntimeDuration,
    getOeeVsYesterday,
    getTotalOutput,
    getVisibleMachineCards,
    isToday,
    sortFloorDeckJobOrders,
} from './floorDeckUtils';
import { getVisibleMonths } from '../../_components/auroraTrendUtils';
import { HomePageTabs } from './HomePageTabs';
import { LossCarousel } from './LossCarousel';
import { isMetricInfoEvent } from './MetricInfo';
import { UtilizationByMachineInfo, UtilizationByTimeInfo } from './UtilizationInfo';
import './FloorDeck.css';

function localDay() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function formatNumber(value) {
    return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function clampPercent(value) {
    return Math.max(0, Math.min(100, Number(value) || 0));
}

// Must stay in step with the viewport-lock media query in FloorDeck.css.
const DECK_LOCK_QUERY = '(min-width: 1100px) and (min-height: 600px)';

// Where the page content actually ends, in document pixels. scrollHeight alone
// is rounded to an integer, which hides sub-pixel overflow — a footer 52.3px
// tall reads as "no overflow" while the browser still draws a scrollbar for the
// leftover 0.3px. getBoundingClientRect keeps the fraction.
function measurePageBottom() {
    const doc = document.documentElement;
    const offset = window.scrollY;
    return Math.max(
        doc.getBoundingClientRect().bottom + offset,
        document.body ? document.body.getBoundingClientRect().bottom + offset : 0,
        doc.scrollHeight
    );
}

// Mirrors the tone precedence in getFloorDeckJobOrderPresentation.
const JOB_LEGEND = [
    { tone: 'on-track', label: 'On track', detail: 'in progress and not past its due date.' },
    { tone: 'behind', label: 'Behind', detail: 'past its due date and still under 100%.' },
    { tone: 'complete', label: 'Complete', detail: 'reached 100% of the planned quantity.' },
    { tone: 'over-target', label: 'Over plan', detail: 'produced more than the planned quantity.' },
];

class FloorDeck extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            date: localDay(),
            summary: null,
            trends: null,
            loadingSummary: true,
            loadingTrends: true,
            summaryError: false,
            trendsError: false,
            jobSort: 'most-behind',
            jobLegendOpen: false,
            machineCategoriesOpen: false,
            // Scheduled available hours are loaded separately from machine status.
            utilizationSettings: null,
            spilloverOpen: false,
            downtimeOpen: false,
            summaryCycle: 0,
            trendsCycle: 0,
        };
        this.dateInput = React.createRef();
        this.jobLegend = React.createRef();
        this.machineCategoriesTrigger = React.createRef();
        this.machineCategoriesClose = React.createRef();
        this.deck = React.createRef();
        this.deckHeightFrame = 0;
        this.syncDeckHeight = this.syncDeckHeight.bind(this);
        this.scheduleDeckHeight = this.scheduleDeckHeight.bind(this);
        this.load = this.load.bind(this);
        this.getMachineStatus = this.getMachineStatus.bind(this);
        this.openDatePicker = this.openDatePicker.bind(this);
        this.preventDateTyping = this.preventDateTyping.bind(this);
        this.toggleJobLegend = this.toggleJobLegend.bind(this);
        this.dismissJobLegend = this.dismissJobLegend.bind(this);
        this.openMachineCategories = this.openMachineCategories.bind(this);
        this.closeMachineCategories = this.closeMachineCategories.bind(this);
        this.dismissMachineCategories = this.dismissMachineCategories.bind(this);
        this.openCategoryInDailyView = this.openCategoryInDailyView.bind(this);
    }

    componentDidMount() {
        this.load();
        this.visibilityHandler = () => {
            if (!document.hidden && isToday(this.state.date)) this.load();
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
        document.addEventListener('mousedown', this.dismissJobLegend);
        document.addEventListener('keydown', this.dismissJobLegend);
        document.addEventListener('keydown', this.dismissMachineCategories);
        this.syncDeckHeight();
        // Second pass once web fonts and the tab indicator have settled, since
        // either can shift where the deck starts.
        this.scheduleDeckHeight();
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(this.scheduleDeckHeight).catch(() => { });
        }
        window.addEventListener('resize', this.scheduleDeckHeight);
        this.pollId = window.setInterval(() => {
            if (!document.hidden && isToday(this.state.date) && !this.state.loadingSummary) this.load();
        }, 60000);
    }

    componentWillUnmount() {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        document.removeEventListener('mousedown', this.dismissJobLegend);
        document.removeEventListener('keydown', this.dismissJobLegend);
        document.removeEventListener('keydown', this.dismissMachineCategories);
        window.removeEventListener('resize', this.scheduleDeckHeight);
        window.cancelAnimationFrame(this.deckHeightFrame);
        window.clearInterval(this.pollId);
    }

    getAccountId() {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}').accountId;
        } catch (error) {
            return null;
        }
    }

    openDatePicker(event) {
        const input = this.dateInput.current;
        if (!input || event.target === input) return;

        try {
            if (typeof input.showPicker === 'function') input.showPicker();
            else input.focus();
        } catch (error) {
            input.focus();
        }
    }

    preventDateTyping(event) {
        event.preventDefault();
    }

    // Sizes the deck to the space the app shell actually leaves it. Rather than
    // adding up the shell's padding and footer — which means assuming its
    // markup — start deliberately too tall and take back exactly what the page
    // reports as overflow. That reads the real chrome whatever sits between the
    // deck and the bottom of the page, at any browser zoom.
    //
    // Measure once from that oversized state and never subtract twice. A page
    // whose min-height floor outruns its content reports the same overflow no
    // matter how much the deck gives up — mobile/TV Chrome resolves `vh`
    // against the toolbar-hidden viewport, so a `100vh` floor stands taller
    // than innerHeight — and compounding the subtraction there shrank the deck
    // until the operations band collapsed to nothing and its panels spilled
    // over the analysis cards. Overshooting the deck is self-limiting instead:
    // the shell below a viewport-tall deck is what the reading is taken from.
    syncDeckHeight() {
        const deck = this.deck.current;
        if (!deck) return;

        if (!window.matchMedia(DECK_LOCK_QUERY).matches) {
            deck.style.removeProperty('--floor-deck-height');
            return;
        }

        const viewport = window.innerHeight;
        deck.style.setProperty('--floor-deck-height', `${viewport}px`);
        const overflow = Math.max(0, measurePageBottom() - viewport);
        // Floor, never round: half a pixel over and the scrollbar is back.
        const height = Math.max(Math.floor(viewport - overflow), 320);
        deck.style.setProperty('--floor-deck-height', `${height}px`);
    }

    scheduleDeckHeight() {
        if (this.deckHeightFrame) return;
        this.deckHeightFrame = window.requestAnimationFrame(() => {
            this.deckHeightFrame = 0;
            this.syncDeckHeight();
        });
    }

    toggleJobLegend() {
        this.setState((state) => ({ jobLegendOpen: !state.jobLegendOpen }));
    }

    // Closes the colour key on Escape or on a click outside it. Bound to the
    // document for the lifetime of the page, so it bails out when already shut.
    dismissJobLegend(event) {
        if (!this.state.jobLegendOpen) return;
        if (event.type === 'keydown' && event.key !== 'Escape') return;
        if (event.type === 'mousedown' && this.jobLegend.current && this.jobLegend.current.contains(event.target)) return;
        this.setState({ jobLegendOpen: false });
    }

    openMachineCategories() {
        this.setState({ machineCategoriesOpen: true }, () => {
            this.machineCategoriesClose.current?.focus();
        });
    }

    closeMachineCategories() {
        this.setState({ machineCategoriesOpen: false }, () => {
            this.machineCategoriesTrigger.current?.focus();
        });
    }

    dismissMachineCategories(event) {
        if (this.state.machineCategoriesOpen && event.key === 'Escape') this.closeMachineCategories();
    }

    openCategoryInDailyView(categoryName) {
        this.setState({ machineCategoriesOpen: false }, () => {
            this.props.onViewChange('pep', 'daily', categoryName);
        });
    }

    load() {
        const company = this.getAccountId();
        if (!company) return;

        const { date } = this.state;
        const summaryRequest = homeService.getFloorDeckPepMetrics({ company, date });
        if (summaryRequest.data) {
            this.setState({ summary: summaryRequest.data, loadingSummary: false, summaryError: false });
        } else {
            this.setState({ loadingSummary: true, summaryError: false });
        }

        summaryRequest.revalidate
            .then((summary) => this.setState((state) => ({ summary, loadingSummary: false, summaryError: false, summaryCycle: state.summaryCycle + 1 })))
            .catch(() => this.setState({ loadingSummary: false, summaryError: true }));

        const settingsRequest = utilizationSettingsService.getSettings();
        if (settingsRequest.data) this.setState({ utilizationSettings: settingsRequest.data });
        settingsRequest.revalidate
            .then((utilizationSettings) => this.setState({ utilizationSettings }))
            // Keep the last saved hours when settings cannot be refreshed.
            .catch(() => this.setState((state) => ({
                utilizationSettings: state.utilizationSettings
                    || { availableHours: DEFAULT_AVAILABLE_HOURS },
            })));

        this.setState({ loadingTrends: true, trendsError: false });
        homeService.fetchFloorDeckTrends({ company, date })
            .then((trends) => this.setState((state) => ({ trends, loadingTrends: false, trendsError: false, trendsCycle: state.trendsCycle + 1 })))
            .catch(() => this.setState({ loadingTrends: false, trendsError: true }));
    }

    getMachineStatus(machine) {
        if (!machine || !machine.totalActOutput || machine.totalActOutput <= 0) return 'idle';
        if (machine.dataInterval !== null && machine.timestamp) {
            const timestamp = new Date(String(machine.timestamp).replace(' ', 'T')).getTime();
            const staleThreshold = (Number(machine.dataInterval) * 60 + 15) * 60 * 1000;
            if (Number.isFinite(timestamp) && Date.now() - timestamp > staleThreshold) return 'idle';
        }
        return 'running';
    }

    renderSemiGauge(percent, tone, size) {
        const fill = `${(clampPercent(percent) * 1.8).toFixed(1)}deg`;
        return (
            <span
                className={`floor-deck__semi floor-deck__semi--${size} floor-deck__semi--${tone}`}
                style={{ '--semi-fill': fill }}
                aria-hidden="true"
            />
        );
    }

    renderJobLegend() {
        const { jobLegendOpen } = this.state;
        return (
            <div className="floor-deck__info" ref={this.jobLegend}>
                <button
                    type="button"
                    className="floor-deck__info-button"
                    aria-expanded={jobLegendOpen}
                    aria-controls="floor-deck-job-legend"
                    aria-label="What the progress bar colours mean"
                    onClick={this.toggleJobLegend}
                >
                    <InfoOutlinedIcon aria-hidden="true" />
                </button>
                {jobLegendOpen ? (
                    <div className="floor-deck__info-popover" id="floor-deck-job-legend">
                        <p className="floor-deck__info-title">Progress bar colours</p>
                        <ul>
                            {JOB_LEGEND.map((entry) => (
                                <li key={entry.tone} className={`floor-deck__info-entry floor-deck__job-item--${entry.tone}`}>
                                    <span className="floor-deck__info-swatch" aria-hidden="true" />
                                    <span><strong>{entry.label}</strong> {entry.detail}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>
        );
    }

    renderOeeDelta(comparison, loading) {
        if (loading && !comparison.available) {
            return <p className="floor-deck__metric-detail">Comparing to yesterday…</p>;
        }
        if (!comparison.available) {
            return <p className="floor-deck__metric-detail">No OEE recorded yesterday</p>;
        }

        const arrow = comparison.direction === 'up' ? '↗' : comparison.direction === 'down' ? '↘' : '→';
        const trendWord = comparison.direction === 'up' ? 'Up' : comparison.direction === 'down' ? 'Down' : 'Unchanged,';
        return (
            <p className={`floor-deck__metric-detail floor-deck__metric-detail--delta floor-deck__metric-detail--${comparison.direction}`}>
                <span aria-hidden="true">{arrow}</span>
                <span className="floor-deck__sr">{trendWord} </span>
                {Math.abs(comparison.delta).toFixed(1)}% vs yesterday
            </p>
        );
    }

    // Compact strip: status, machine, its job, OEE against target and job
    // progress. The per-loss breakdown lives in Daily Overview / View All.
    renderMachineCard(machine, index, target) {
        const status = this.getMachineStatus(machine);
        const oee = clampPercent(machine.oee);
        const progress = clampPercent(machine.progress);
        const oeeTone = oee >= Number(target) ? 'on' : 'below';
        return (
            <article className="floor-deck__machine-card" role="listitem" key={machine.machine} style={{ '--stagger-index': index }}>
                <div className="floor-deck__machine-heading">
                    <span className={`floor-deck__status-dot floor-deck__status-dot--${status}`} aria-hidden="true" />
                    <strong>{machine.machine}</strong>
                    <span className="floor-deck__sr">{status === 'running' ? 'Running' : 'Idle'}</span>
                    <b className={`floor-deck__machine-oee floor-deck__machine-oee--${oeeTone}`}>
                        {oee.toFixed(0)}<small>% OEE</small>
                    </b>
                </div>
                <p className="floor-deck__machine-job">{machine.jobOrder || 'No active job'}</p>
                <div className="floor-deck__machine-progress">
                    <div className="floor-deck__progress" aria-label={`${machine.machine} job progress ${progress.toFixed(0)} percent`}>
                        <span style={{ width: `${progress}%` }} />
                    </div>
                    <small>{progress.toFixed(0)}%</small>
                </div>
            </article>
        );
    }

    renderMachineCategoriesModal(machines, target, availableHours) {
        if (!this.state.machineCategoriesOpen) return null;

        const categories = groupMachinesByCategory(machines);

        return (
            <div className="floor-deck__machine-modal-backdrop" onMouseDown={(event) => {
                if (event.target === event.currentTarget) this.closeMachineCategories();
            }}>
                <section id="floor-deck-machine-categories" className="floor-deck__machine-modal" role="dialog" aria-modal="true" aria-labelledby="floor-deck-machine-categories-title">
                    <header className="floor-deck__machine-modal-header">
                        <div>
                            <h2 id="floor-deck-machine-categories-title">Machine Categories</h2>
                            <p>{machines.length} machine{machines.length === 1 ? '' : 's'} across {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}</p>
                        </div>
                        <button ref={this.machineCategoriesClose} type="button" className="floor-deck__machine-modal-close" aria-label="Close machine categories" onClick={this.closeMachineCategories}>
                            <CloseRoundedIcon aria-hidden="true" />
                        </button>
                    </header>
                    <div className="floor-deck__machine-modal-content">
                        {categories.map((category, index) => {
                            const activeMachines = category.machines.filter((machine) => this.getMachineStatus(machine) === 'running');
                            const utilizationByMachine = (activeMachines.length / category.machines.length) * 100;
                            const utilizationByTime = getUtilizationByTime(category.machines, availableHours);
                            const average = (value) => activeMachines.length
                                ? activeMachines.reduce((total, machine) => total + Number(value(machine) || 0), 0) / activeMachines.length
                                : 0;
                            const oeeMachines = activeMachines.filter(machine => Number(machine.oee) > 0);
                            const averageOee = clampPercent(oeeMachines.length
                                ? oeeMachines.reduce((sum, machine) => sum + Number(machine.oee), 0) / oeeMachines.length : 0);
                            const averageOutput = average((machine) => machine.totalActOutput ?? machine.actualOutput);
                            const averageProgress = clampPercent(average((machine) => machine.progress));
                            const oeeTone = averageOee >= Number(target) ? 'on' : 'below';
                            return (
                                <section
                                    className="floor-deck__machine-category"
                                    key={category.name}
                                    role="button"
                                    tabIndex="0"
                                    aria-label={`View ${category.name} machines in PE&P Daily`}
                                    onClick={(event) => {
                                        // The formula buttons and popovers sit inside the card.
                                        if (!isMetricInfoEvent(event)) this.openCategoryInDailyView(category.name);
                                    }}
                                    onKeyDown={(event) => {
                                        if (isMetricInfoEvent(event)) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            this.openCategoryInDailyView(category.name);
                                        }
                                    }}
                                >
                                    <header>
                                        <h3>{category.name}</h3>
                                        <span>{category.machines.length} machine{category.machines.length === 1 ? '' : 's'}</span>
                                    </header>
                                    <dl className="floor-deck__machine-category-metrics">
                                        <div>
                                            <dt>
                                                Utilization by Machine
                                                <UtilizationByMachineInfo
                                                    id={`utilization-by-machine-${index}`}
                                                    running={activeMachines.length}
                                                    total={category.machines.length}
                                                />
                                            </dt>
                                            <dd>{utilizationByMachine.toFixed(1)}%</dd>
                                        </div>
                                        <div>
                                            <dt>
                                                Utilization by Time
                                                <UtilizationByTimeInfo
                                                    id={`utilization-by-time-${index}`}
                                                    machines={category.machines}
                                                    availableHours={availableHours}
                                                />
                                            </dt>
                                            <dd>{availableHours ? `${utilizationByTime.toFixed(1)}%` : '—'}</dd>
                                        </div>
                                        <div>
                                            <dt>Average OEE</dt>
                                            <dd className={`floor-deck__machine-oee floor-deck__machine-oee--${oeeTone}`}>{averageOee.toFixed(1)}%</dd>
                                        </div>
                                        <div>
                                            <dt>Average Output</dt>
                                            <dd>{formatNumber(averageOutput)} pcs</dd>
                                        </div>
                                        <div>
                                            <dt>Average Progress</dt>
                                            <dd>{averageProgress.toFixed(1)}%</dd>
                                        </div>
                                    </dl>
                                    <div className="floor-deck__machine-category-progress">
                                        <div className="floor-deck__progress" aria-label={`${category.name} average progress ${averageProgress.toFixed(1)} percent`}>
                                            <span style={{ width: `${averageProgress}%` }} />
                                        </div>
                                    </div>
                                </section>
                            );
                        })}
                        {categories.length === 0 ? <p className="floor-deck__empty">No machine data is available for this day.</p> : null}
                    </div>
                </section>
            </div>
        );
    }

    renderLossCard(metric, year, index) {
        // Months always span Jan–Dec, so trim the months that have not happened
        // yet — the same window the PE&P yearly cards average over.
        const months = getVisibleMonths(metric.months || [], year);
        const title = String(metric.title || '').toLowerCase();
        const lossTone = title.includes('availability') ? 'availability' : title.includes('technical') ? 'technical' : title.includes('organization') ? 'organization' : 'quality';
        const values = months.filter(month => month[1] != null).map((month) => Number(month[1]));
        const highest = Math.max(...values, 1);
        const points = values.map((value, i) => `${(i / Math.max(values.length - 1, 1)) * 100},${100 - ((value / highest) * 84 + 8)}`).join(' ');
        const ytd = values.length ? Math.round(values.reduce((total, value) => total + value, 0) / values.length) : 'N/A';
        return (
            <article
                className={`floor-deck__analysis-card floor-deck__analysis-card--loss floor-deck__analysis-card--loss-${lossTone} floor-deck__analysis-card--link`}
                key={metric.title}
                style={{ '--stagger-index': index }}
                role="button"
                tabIndex="0"
                onClick={() => this.props.onViewChange('pep', 'yearly')}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        this.props.onViewChange('pep', 'yearly');
                    }
                }}
            >
                <p className="floor-deck__card-title">{metric.title}</p>
                <strong className="floor-deck__loss-value">{ytd}<small>YTD {year} · %</small></strong>
                <svg className="floor-deck__sparkline" viewBox="0 0 100 100" role="img" aria-label={`${metric.title} year to date trend`} preserveAspectRatio="none">
                    <polyline points={points || '0,92 100,92'} />
                </svg>
                <div className="floor-deck__chart-labels">
                    <span>{months.length ? months[0][0] : 'Jan'}</span>
                    <span>{months.length ? months[months.length - 1][0] : 'Dec'}</span>
                </div>
            </article>
        );
    }

    render() {
        const { activeView = 'floor-deck', onViewChange } = this.props;
        const { date, summary, trends, loadingSummary, loadingTrends, summaryError, trendsError, summaryCycle, trendsCycle, jobSort, machineCategoriesOpen, utilizationSettings } = this.state;
        const data = summary || { summary: {}, machines: [], jobOrders: [] };
        const metrics = data.summary || {};
        const machines = data.machines || [];
        const runningCount = machines.filter((machine) => this.getMachineStatus(machine) === 'running').length;
        const idleCount = Math.max(machines.length - runningCount, 0);
        const visibleMachines = getVisibleMachineCards(machines, (machine) => this.getMachineStatus(machine) === 'running');
        const currentMonth = getCurrentMonthRange(date);
        const oeeTrend = aggregateDailyOee(trends && trends.oeeRows, currentMonth.daysInMonth);
        const losses = trends && trends.losses ? trends.losses : [];
        const availability = trends && trends.availability;
        const yearlyCards = [availability, ...losses].filter(Boolean);
        const target = getCurrentMonthTarget(readCachedMonthlyTargets().oee);
        const targetValue = clampPercent(target);
        const year = date.slice(0, 4);
        const dateLabel = getFloorDeckDateLabel(date);

        const oee = clampPercent(metrics.oee);
        const oeeVsYesterday = getOeeVsYesterday(oeeTrend, date, metrics.oee);
        const totalOutput = getTotalOutput(metrics);
        const targetOutput = Number(metrics.targetOutput || 0);
        const outputPct = targetOutput > 0 ? (totalOutput / targetOutput) * 100 : 0;
        const yieldPct = totalOutput > 0 ? (Number(metrics.actualOutput || 0) / totalOutput) * 100 : 0;
        const downtime = getDowntimeDuration(metrics.machineDownSeconds);
        // The backend selects active machines for today and preserves past-day history.
        const utilizationReady = utilizationSettings !== null;
        const availableHours = utilizationReady ? utilizationSettings.availableHours : null;
        const utilizationMachines = machines;
        const utilizationRunning = utilizationMachines.filter((machine) => this.getMachineStatus(machine) === 'running').length;
        const totalMachines = utilizationMachines.length;
        const runningPct = totalMachines > 0 ? (utilizationRunning / totalMachines) * 100 : 0;
        const jobOrders = sortFloorDeckJobOrders(data.jobOrders, jobSort);

        return (
            <main className="floor-deck floor-deck--deck" ref={this.deck}>
                <header className="floor-deck__header">
                    <HomePageTabs activeView={activeView} onViewChange={onViewChange} />
                    <label className="floor-deck__date-control" onClick={this.openDatePicker}>
                        <CalendarMonthRoundedIcon className="floor-deck__date-icon" aria-hidden="true" />
                        <input ref={this.dateInput} type="date" value={date} aria-label="Floor Deck day" onKeyDown={this.preventDateTyping} onChange={(event) => this.setState({ date: event.target.value }, this.load)} />
                        {/* <small>{formatDayRange(date)}</small> */}
                    </label>
                    <div className={`floor-deck__live floor-deck__live--${isToday(date) ? 'live' : 'historic'}`}>
                        <span aria-hidden="true" />{dateLabel}
                    </div>
                </header>

                {summaryError ? <button className="floor-deck__retry" type="button" onClick={this.load}>Retry Floor Deck data</button> : null}
                <section key={summaryCycle} className={`floor-deck__metric-grid ${loadingSummary ? 'is-loading' : ''}`} aria-busy={loadingSummary}>
                    <section className="floor-deck__metric floor-deck__metric--oee" style={{ '--stagger-index': 0 }}>
                        <p className="floor-deck__metric-label">Overall Equipment Effectiveness</p>
                        <div className="floor-deck__metric-main">
                            <div className="floor-deck__metric-value">{Number(metrics.oee || 0).toFixed(1)}<small>%</small></div>
                        </div>
                        {this.renderOeeDelta(oeeVsYesterday, loadingTrends)}
                        <div className="floor-deck__progress-row">
                            <div className="floor-deck__progress"><span style={{ width: `${oee}%` }} /></div>
                        </div>
                    </section>
                    <section className="floor-deck__metric floor-deck__metric--output" style={{ '--stagger-index': 1 }}>
                        <p className="floor-deck__metric-label">Total Output</p>
                        <div className="floor-deck__metric-main">
                            <div className="floor-deck__metric-value">{formatNumber(totalOutput)}<small> pcs</small></div>
                        </div>
                        <p className="floor-deck__metric-detail">Target: {formatNumber(targetOutput)} pcs</p>
                        <div className="floor-deck__progress-row">
                            <div className="floor-deck__progress"><span style={{ width: `${Math.min(outputPct, 100)}%` }} /></div>
                            <b>{outputPct.toFixed(1)}%</b>
                        </div>
                    </section>
                    <section className="floor-deck__metric floor-deck__metric--good-output" style={{ '--stagger-index': 2 }}>
                        <p className="floor-deck__metric-label">Good Output</p>
                        <div className="floor-deck__metric-main">
                            <div className="floor-deck__metric-value">{formatNumber(metrics.actualOutput)}<small> pcs</small></div>
                        </div>
                        <p className="floor-deck__metric-detail floor-deck__metric-detail--good">Yield: {yieldPct.toFixed(1)}%</p>
                        <div className="floor-deck__progress-row">
                            <div className="floor-deck__progress"><span style={{ width: `${clampPercent(yieldPct)}%` }} /></div>
                        </div>
                    </section>
                    <button ref={this.machineCategoriesTrigger} type="button" className="floor-deck__metric floor-deck__metric--machines floor-deck__metric--button" style={{ '--stagger-index': 3 }} onClick={this.openMachineCategories} aria-label="Show all machine categories" aria-expanded={this.state.machineCategoriesOpen} aria-controls="floor-deck-machine-categories">
                        <div className="floor-deck__metric-label-row">
                            {/* Machines Running */}
                            <p className="floor-deck__metric-label">Machine Utilization</p>
                            <ExpandMoreRoundedIcon className={`floor-deck__machine-expand-icon${machineCategoriesOpen ? ' is-open' : ''}`} aria-hidden="true" />
                        </div>
                        <div className="floor-deck__metric-main">
                            <div className="floor-deck__metric-value">{loadingSummary && !summary ? '—' : `${utilizationRunning} / ${totalMachines}`}</div>
                        </div>
                        <div className="floor-deck__progress-row">
                            <div className="floor-deck__progress"><span style={{ width: `${Math.min(runningPct, 100)}%` }} /></div>
                            <b>{runningPct.toFixed(0)}%</b>
                        </div>
                    </button>
                    <button type="button" className="floor-deck__metric floor-deck__metric--downtime floor-deck__metric--button"
                        style={{ '--stagger-index': 4 }} onClick={() => this.setState({ downtimeOpen: true })}
                        disabled={loadingSummary || summaryError || !downtime}
                        aria-label="Show Machine Downtime breakdown" aria-haspopup="dialog">
                        <div className="floor-deck__metric-label-row">
                            <p className="floor-deck__metric-label">Machine Downtime</p>
                            <ExpandMoreRoundedIcon className={`floor-deck__machine-expand-icon${this.state.downtimeOpen ? ' is-open' : ''}`} aria-hidden="true" />
                        </div>
                        <div className="floor-deck__metric-main">
                            <div className="floor-deck__metric-value">
                                {downtime ? <>{downtime.hours} <small>h</small> {downtime.minutes} <small>m</small></> : '—'}
                            </div>
                        </div>
                        <p className="floor-deck__metric-detail">
                            {downtime ? getDowntimeDetail(data.machineDownSubmissions, metrics.machineDownPcs) : ''}
                        </p>
                    </button>
                    <button type="button" className="floor-deck__metric floor-deck__metric--spillover floor-deck__metric--button"
                        style={{ '--stagger-index': 5 }} onClick={() => this.setState({ spilloverOpen: true })}
                        disabled={loadingSummary || summaryError || metrics.spilloverGoodOutput == null}
                        aria-label="Show Spillover submissions" aria-haspopup="dialog">
                        <div className="floor-deck__metric-label-row">
                            <p className="floor-deck__metric-label">Spillover</p>
                            <ExpandMoreRoundedIcon className={`floor-deck__machine-expand-icon${this.state.spilloverOpen ? ' is-open' : ''}`} aria-hidden="true" />
                        </div>
                        <div className="floor-deck__metric-main">
                            <div className="floor-deck__metric-value">
                                {metrics.spilloverGoodOutput == null ? '-' : formatNumber(metrics.spilloverGoodOutput)}<small> pcs</small>
                            </div>
                        </div>
                        <p className="floor-deck__metric-detail">Good output excluded from OEE</p>
                    </button>
                </section>

                <SpilloverModal show={this.state.spilloverOpen} onClose={() => this.setState({ spilloverOpen: false })}
                    date={this.state.date} submissions={(this.state.summary || {}).spilloverSubmissions || []} />
                <MachineDowntimeModal show={this.state.downtimeOpen} onClose={() => this.setState({ downtimeOpen: false })}
                    date={date} submissions={data.machineDownSubmissions || []}
                    totalSeconds={metrics.machineDownSeconds} totalPcs={metrics.machineDownPcs} />
                {this.renderMachineCategoriesModal(utilizationMachines, target, availableHours)}

                <section className="floor-deck__operations-grid">
                    <article className="floor-deck__panel floor-deck__status-panel">
                        <header className="floor-deck__panel-heading">
                            <div>
                                <h2>Machine / Line Status</h2>
                                <p className="floor-deck__legend">
                                    <span className="floor-deck__legend-item floor-deck__legend-item--running">Running ({runningCount})</span>
                                    <span className="floor-deck__legend-item floor-deck__legend-item--idle">Idle ({idleCount})</span>
                                </p>
                            </div>
                            <Link to="/daily-overview">View All</Link>
                        </header>
                        <div key={summaryCycle} className={`floor-deck__machine-grid ${loadingSummary ? 'is-loading' : ''}`} role="list" aria-label="Machine and line status" tabIndex="0">
                            {visibleMachines.map((machine, index) => this.renderMachineCard(machine, index, target))}
                            {!loadingSummary && visibleMachines.length === 0 ? <p className="floor-deck__empty">No machine data is available for this day.</p> : null}
                        </div>
                    </article>

                    <article className="floor-deck__panel floor-deck__jobs-panel">
                        <header className="floor-deck__panel-heading">
                            <div className="floor-deck__panel-title">
                                <h2>Job Orders (Active)</h2>
                                {this.renderJobLegend()}
                            </div>
                            <div className="floor-deck__jobs-actions">
                                <label className="floor-deck__job-sort">
                                    <span className="floor-deck__sr">Sort active job orders</span>
                                    <select value={jobSort} onChange={(event) => this.setState({ jobSort: event.target.value })}>
                                        <option value="most-behind">Sort: Most behind</option>
                                        <option value="progress">Sort: Progress</option>
                                        <option value="due-date">Sort: Due date</option>
                                    </select>
                                </label>
                                <Link to="/jobOrderSummaryReport">View All</Link>
                            </div>
                        </header>
                        <div key={jobSort} className={`floor-deck__job-list ${loadingSummary ? 'is-loading' : ''}`} role="list" aria-label="Active job orders" tabIndex="0">
                            {jobOrders.map((job, index) => {
                                const presentation = getFloorDeckJobOrderPresentation(job, date);
                                const varianceSign = presentation.variance >= 0 ? '+' : '';
                                return (
                                    <article className={`floor-deck__job-item floor-deck__job-item--${presentation.tone}`} role="listitem" key={job.jobNo} style={{ '--stagger-index': index }}>
                                        <div className="floor-deck__job-id">
                                            <strong>{job.jobNo}</strong>
                                            <span>{job.partNo || 'Part not available'}</span>
                                        </div>
                                        <div className="floor-deck__job-track">
                                            <div className="floor-deck__job-bar" aria-label={`${job.jobNo} progress ${presentation.progress.toFixed(1)} percent`}><span style={{ width: `${presentation.barProgress}%` }} /></div>
                                            <span className="floor-deck__job-qty">
                                                <b>{formatNumber(job.actualOutput)}</b> / {formatNumber(job.plannedOutput)} pcs · <b className="floor-deck__job-variance">{varianceSign}{formatNumber(presentation.variance)}</b>
                                            </span>
                                        </div>
                                        <b className="floor-deck__job-percent">{presentation.progress.toFixed(1)}<small>%</small></b>
                                        <em className="floor-deck__job-due">{presentation.dueLabel}</em>
                                    </article>
                                );
                            })}
                            {!loadingSummary && jobOrders.length === 0 ? <p className="floor-deck__empty">No active job orders for this day.</p> : null}
                        </div>
                    </article>
                </section>

                <section className="floor-deck__analysis" aria-busy={loadingTrends}>
                    <header className="floor-deck__analysis-heading">
                        <h2>Tactical Analysis</h2>
                        <p>OEE trend · current month — availability &amp; losses YTD {year}</p>
                    </header>
                    {trendsError ? <button className="floor-deck__retry" type="button" onClick={this.load}>Retry trends</button> : null}
                    <div className="floor-deck__analysis-grid">
                        <Link to="/dailyoeereport" className="floor-deck__analysis-card floor-deck__analysis-card--dark floor-deck__oee-trend floor-deck__analysis-card--link" style={{ '--stagger-index': 0 }} aria-label="View Daily OEE Report">
                            <p className="floor-deck__card-title">Production Daily OEE Performance <span>· daily target {target}%</span></p>
                            <div className="floor-deck__oee-bars" style={{ '--target-line': `${100 - targetValue}%` }}>
                                {oeeTrend.map((value, index) => <span key={index} className={value >= Number(target) ? 'is-on-target' : 'is-below-target'} style={{ '--bar-height': `${Math.max(value, 2)}%` }} />)}
                            </div>
                            <div className="floor-deck__chart-labels"><span>1</span><span>day of month</span><span>{currentMonth.daysInMonth}</span></div>
                        </Link>
                        <LossCarousel metrics={yearlyCards} renderCard={(metric) => this.renderLossCard(metric, year, 0)} />
                    </div>
                </section>
            </main>
        );
    }
}

export { FloorDeck };
